// src/steps/StepDayPoints.tsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';

import logosImg from '../assets/web_logos_bandera.svg';
import houseIconUrl from '../assets/house.png';

import { useItinerary } from '../hooks/useItinerary';
import styles from '../styles/ItineraryForm.module.css';
import { CARTO_ATTRIBUTION, CARTO_BASEMAP_URL } from '../config/maps';

import shp from 'shpjs';
import type { FeatureCollection } from 'geojson';

/* ------------------------------------------------------------------ */
/*  Icono “casita”                                                    */
/* ------------------------------------------------------------------ */
const houseIcon = new L.Icon({
  iconUrl: houseIconUrl,
  iconRetinaUrl: houseIconUrl,
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 29],
  iconAnchor: [10, 34],
});

/* ------------------------------------------------------------------ */
/*  TIPOS                                                             */
/* ------------------------------------------------------------------ */
type Dia = number;
type PuntoTipo = 'start' | 'end';

interface Props {
  days: Dia[];
  onBack: () => void;
  onFinish: () => void;
  loading: boolean;
}

interface Municipio {
  nombre: string;
  lat: number;
  lon: number;
}

/* ------------------------------------------------------------------ */
/*  Sombreado Shapefile + nombres (desde el .zip)                      */
/*  - muestra tooltip con properties.NOMBRE al pasar el ratón          */
/* ------------------------------------------------------------------ */
const ShapefileOverlay: React.FC<{
  url: string;
  fitOnLoad?: boolean;
  style?: L.PathOptions;
  paneName?: string;
}> = ({ url, fitOnLoad = false, style, paneName = 'shade-pane' }) => {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    // pane por debajo de markers, pero interactivo (hover) para tooltips
    if (!map.getPane(paneName)) {
      const p = map.createPane(paneName);
      p.style.zIndex = '350';
      p.style.pointerEvents = 'auto'; // ✅ necesario para hover/tooltip
    }

    let cancelled = false;

    (async () => {
      try {
        const geojson = (await shp(url)) as FeatureCollection;
        if (cancelled) return;

        const layer = L.geoJSON(geojson as any, {
          pane: paneName,
          interactive: true, // ✅ necesario para recibir mouseover
          bubblingMouseEvents: false,
          style: {
            color: '#e74c3c',
            weight: 1,
            fillColor: '#e74c3c',
            fillOpacity: 0.35,
            ...style,
          },
          onEachFeature: (feature: any, lyr: any) => {
           
            const nombre = feature?.properties?.NOMBRE;
            if (!nombre) return;

           
          },
        });

        layer.addTo(map);
        layerRef.current = layer;

        if (fitOnLoad) {
          const b = layer.getBounds();
          if (b.isValid()) map.fitBounds(b, { padding: [20, 20] });
        }
      } catch (e) {
        console.error('Error cargando shapefile:', e);
      }
    })();

    return () => {
      cancelled = true;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [url, fitOnLoad, paneName, map, style]);

  return null;
};

/* ================================================================== */
/*  COMPONENTE                                                        */
/* ================================================================== */
const StepDayPoints: React.FC<Props> = ({ days, onBack, onFinish, loading }) => {
  const { dayStarts, setDayStarts, dayEnds, setDayEnds } = useItinerary();

  const [selectedDay, setSelectedDay] = useState<Dia>(days[0]);
  const [selectedKind, setSelectedKind] = useState<PuntoTipo>('start');
  const [marker, setMarker] = useState<L.LatLng | null>(null);

  // Municipios
  const [municipios, setMunicipios] = useState<Municipio[]>([]);

  // Queries por día/tipo
  const [startQueryByDay, setStartQueryByDay] = useState<Record<number, string>>({});
  const [endQueryByDay, setEndQueryByDay] = useState<Record<number, string>>({});

  // Carga municipios (para el datalist)
  useEffect(() => {
    const base = (process.env.PUBLIC_URL || '') + '/';
    fetch(`${base}data/municipios.json`)
      .then((r) => r.json())
      .then((data: Municipio[]) => setMunicipios(data))
      .catch((err) => console.error('No se pudo cargar /data/municipios.json', err));
  }, []);

  const normalize = (s: string) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const muniOptions = useMemo(() => municipios.slice(0, 5000), [municipios]);

  // Cuando cambias selectedDay/Kind, cargar marker si ya había punto guardado
  useEffect(() => {
    const saved = selectedKind === 'start' ? dayStarts[selectedDay] : dayEnds[selectedDay];
    setMarker(saved ? L.latLng(saved.latitud, saved.longitud) : null);
  }, [selectedDay, selectedKind, dayStarts, dayEnds]);

  // Helper: obtener el texto del input correspondiente
  const getQueryFor = (day: Dia, kind: PuntoTipo) => {
    if (kind === 'start') return startQueryByDay[day] ?? '';
    return endQueryByDay[day] ?? '';
  };

  const setQueryFor = (day: Dia, kind: PuntoTipo, value: string) => {
    if (kind === 'start') {
      setStartQueryByDay((prev) => ({ ...prev, [day]: value }));
    } else {
      setEndQueryByDay((prev) => ({ ...prev, [day]: value }));
    }
  };

  // Helper: resolver municipio por nombre (exacto normalizado)
  const resolveMunicipio = (name: string): Municipio | null => {
    const q = normalize(name);
    if (!q) return null;
    return municipios.find((m) => normalize(m.nombre) === q) || null;
  };

  // Auto-advance: Inicio -> Fin -> Inicio siguiente -> ...
  const advanceAfterSave = (day: Dia, kind: PuntoTipo) => {
    if (kind === 'start') {
      setSelectedDay(day);
      setSelectedKind('end');
      return;
    }

    const idx = days.indexOf(day);
    const isLast = idx === days.length - 1;
    if (isLast) {
      setSelectedDay(day);
      setSelectedKind('end');
      return;
    }

    const nextDay = days[idx + 1];
    setSelectedDay(nextDay);
    setSelectedKind('start');
  };

  // Guardar punto (core): guarda en context y actualiza query visible
  const commitPoint = (day: Dia, kind: PuntoTipo, latlng: L.LatLng, municipioNombre?: string) => {
    const nombre =
      municipioNombre && municipioNombre.trim()
        ? municipioNombre.trim()
        : getQueryFor(day, kind).trim() || `${kind === 'start' ? 'Inicio' : 'Fin'} día ${day}`;

    const punto = {
      id: -1,
      nombre,
      tipo: kind === 'start' ? 'Inicio' : 'Fin',
      descripcion: '',
      latitud: latlng.lat,
      longitud: latlng.lng,
    };

    if (kind === 'start') {
      setDayStarts((prev) => ({ ...prev, [day]: punto }));
      setQueryFor(day, 'start', nombre);
    } else {
      setDayEnds((prev) => ({ ...prev, [day]: punto }));
      setQueryFor(day, 'end', nombre);
    }
  };

  // Click en mapa = marker
  const LocationSelector: React.FC = () => {
    useMapEvents({
      click: (e) => setMarker(e.latlng),
    });

    if (!marker) return null;

    return (
      <Marker position={marker} icon={houseIcon}>
        <Tooltip permanent offset={[0, -40]} direction="top">
          <div className={styles.tooltipContent}>
            {selectedKind === 'start' ? 'Inicio' : 'Fin'} día {selectedDay}
          </div>
        </Tooltip>
      </Marker>
    );
  };

  const CenterOnMarker: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      setTimeout(() => map.invalidateSize(), 0);
    }, [map]);

    useEffect(() => {
      if (marker) map.setView(marker, map.getZoom());
    }, [marker, map]);

    return null;
  };

  // Guardar desde el mapa
  const saveFromMap = () => {
    if (!marker) {
      alert('Haz clic en el mapa o elige un municipio para marcar la ubicación');
      return;
    }
    commitPoint(selectedDay, selectedKind, marker);
    advanceAfterSave(selectedDay, selectedKind);
  };

  const clearPoint = () => {
    const day = selectedDay;
    const kind = selectedKind;

    const has = kind === 'start' ? !!dayStarts[day] : !!dayEnds[day];
    if (!has) return;

    if (kind === 'start') {
      setDayStarts((prev) => {
        const c = { ...prev };
        delete c[day];
        return c;
      });
      setStartQueryByDay((prev) => {
        const c = { ...prev };
        delete c[day];
        return c;
      });
    } else {
      setDayEnds((prev) => {
        const c = { ...prev };
        delete c[day];
        return c;
      });
      setEndQueryByDay((prev) => {
        const c = { ...prev };
        delete c[day];
        return c;
      });
    }

    setMarker(null);
  };

  // Guardar desde una fila (elige municipio + guarda + avanza)
  const saveFromRow = (day: Dia, kind: PuntoTipo) => {
    const q = getQueryFor(day, kind);
    const muni = resolveMunicipio(q);

    if (!muni) {
      alert('Municipio no encontrado. Elige uno del desplegable o escribe el nombre exacto.');
      return;
    }

    const latlng = L.latLng(muni.lat, muni.lon);
    setMarker(latlng);

    commitPoint(day, kind, latlng, muni.nombre);
    setSelectedDay(day);
    setSelectedKind(kind);

    advanceAfterSave(day, kind);
  };

  const focusRow = (day: Dia, kind: PuntoTipo) => {
    setSelectedDay(day);
    setSelectedKind(kind);

    const saved = kind === 'start' ? dayStarts[day] : dayEnds[day];
    if (saved) setMarker(L.latLng(saved.latitud, saved.longitud));
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        onFinish();
      }}
    >
      <div className={styles.content}>
        <p className={styles.sectionIntro}>(Opcional) Selecciona inicio y/o fin de cada jornada.</p>

        <div className={styles.dayPointsExactLayout}>
          {/* MAPA */}
          <div className={styles.dayPointsExactMapCol}>
            <MapContainer
              center={[42.7575, -3.2937]}
              zoom={9}
              className={styles.dayPointsExactMap}
            >
              <TileLayer
                attribution={CARTO_ATTRIBUTION}
                url={CARTO_BASEMAP_URL}
              />

              {/* sombreado rojo + tooltip con NOMBRE (del zip) */}
              <ShapefileOverlay url="/data/Municipios.zip" fitOnLoad={false} />

              <LocationSelector />
              <CenterOnMarker />
            </MapContainer>

            <div className={styles.mapButtons}>
              <button
                type="button"
                onClick={saveFromMap}
                disabled={!marker}
                className={styles.saveButton}
              >
                Guardar punto
              </button>

              <button
                type="button"
                onClick={clearPoint}
                disabled={selectedKind === 'start' ? !dayStarts[selectedDay] : !dayEnds[selectedDay]}
                className={styles.cancelButton}
              >
                Borrar selección
              </button>
            </div>
          </div>

          {/* TABLA DERECHA */}
          <div className={styles.dayPointsExactTableCol}>
            <div className={styles.dayPointsExactHeaders}>
              <div className={styles.dayPointsExactHeader}>Inicio</div>
              <div className={styles.dayPointsExactHeader}>Fin</div>
            </div>

            <div className={styles.dayPointsExactRows}>
              {days.map((d) => (
                <div key={d} className={styles.dayPointsExactRow}>
                  {/* Inicio */}
                  <div className={styles.dayPointsExactCell}>
                    <div className={styles.dayPointsExactLine}>
                      <span className={styles.dayPointsExactDayLabel}>Día:</span>
                      <span className={styles.dayPointsExactDayValue}>{d}</span>

                      <span className={styles.dayPointsExactFieldLabel}>Nombre del municipio:</span>
                      <input
                        list="municipios-list"
                        value={getQueryFor(d, 'start')}
                        onChange={(e) => setQueryFor(d, 'start', e.target.value)}
                        onFocus={() => focusRow(d, 'start')}
                        className={styles.dayPointsExactInput}
                      />

                      <button
                        type="button"
                        className={styles.saveButton}
                        onClick={() => saveFromRow(d, 'start')}
                      >
                        Guardar punto
                      </button>
                    </div>
                  </div>

                  {/* Fin */}
                  <div className={styles.dayPointsExactCell}>
                    <div className={styles.dayPointsExactLine}>
                      <span className={styles.dayPointsExactFieldLabel}>Nombre del municipio:</span>
                      <input
                        list="municipios-list"
                        value={getQueryFor(d, 'end')}
                        onChange={(e) => setQueryFor(d, 'end', e.target.value)}
                        onFocus={() => focusRow(d, 'end')}
                        className={styles.dayPointsExactInput}
                      />

                      <button
                        type="button"
                        className={styles.saveButton}
                        onClick={() => saveFromRow(d, 'end')}
                      >
                        Guardar punto
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <datalist id="municipios-list">
              {muniOptions.map((m) => (
                <option key={m.nombre} value={m.nombre} />
              ))}
            </datalist>
          </div>
        </div>

        <div className={styles.buttons}>
          <button type="button" onClick={onBack} className={styles.nextButton}>
            Atrás
          </button>
          <button type="submit" disabled={loading} className={styles.nextButton}>
            {loading ? 'Cargando…' : 'Finalizar'}
          </button>
        </div>

        {/* Saltar: borra puntos y avanza */}
        <div style={{ width: '100%', maxWidth: 800, display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            className={styles.nextButton}
            style={{ maxWidth: 360 }}
            disabled={loading}
            onClick={() => {
              setDayStarts({});
              setDayEnds({});
              setStartQueryByDay({});
              setEndQueryByDay({});
              setMarker(null);
              onFinish();
            }}
          >
            Saltar este paso
          </button>
        </div>

        <div className={styles.footerLogos}>
          <img src={logosImg} alt="NextGenerationEU" />
        </div>
      </div>
    </form>
  );
};

export default StepDayPoints;
